import type { MouseEvent } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Circle } from 'elements/new-design/Shapes';
import { ArrowRightIconDark } from 'elements/new-design/icons/SymbolIcons';
import {
  TxtSmallBoldDarkResp,
  fontColorAccent,
  fontColorDark,
  fontFamilyMedium,
  fontSizeSmallResp,
  fontSizeMediumResp,
  fontFamilyBold,
  fontLargeStatic,
  TxtMediumRegularLightResp,
} from '../Typography';

type LinkProps = {
  text?: string;
  url: string;
  color?: string;
  external?: boolean;
  onLinkClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

type StyleProps = {
  color: string;
};

const LinkMediumResp = styled.span`
  ${fontFamilyMedium}
  ${fontSizeMediumResp}
`;

export const LinkMediumBoldResp = styled.span`
  ${fontFamilyBold}
  ${fontSizeMediumResp}
`;

const LinkTxtLargeDarkStatic = styled.span`
  ${fontColorDark}
  ${fontFamilyMedium}
  ${fontLargeStatic}
`;

export const LinkUnderline = styled(Link)`
  ${(_: StyleProps) => ''}
  padding-bottom: 0.4rem;
  border-bottom: ${(props) =>
    props.color === 'light' ? props.theme.newconstants.borderLightThin : props.theme.newconstants.borderDarkThin};
  :hover,
  :focus-visible {
    box-shadow: none;
    border-bottom: ${(props) =>
      props.color === 'light' ? props.theme.newconstants.borderLightThick : props.theme.newconstants.borderDarkThick};
  }
`;

const LinkNoUnderline = styled(LinkUnderline)`
  border-bottom: ${(props) => props.theme.newconstants.borderTransparantThick};
  :hover,
  :focus-visible {
    border-bottom: ${(props) =>
      props.color === 'light' ? props.theme.newconstants.borderLightThick : props.theme.newconstants.borderDarkThick};
  }
`;

const LinkUnderlineInline = styled(LinkUnderline)`
  ${(_: StyleProps) => ''}
  padding-bottom: 0;
`;

export const LinkCircle = styled(Circle)`
  background: inherit;
  border: ${(props) => props.theme.newconstants.borderDarkThin};
`;

const ExternalLinkStyled = styled.a`
  :hover,
  :focus-visible {
    box-shadow: none;

    ${LinkCircle} {
      border: ${(props) => props.theme.newconstants.borderDarkThick};
    }
  }
`;

export const IconCircleLink = styled(Link)`
  :hover,
  :focus-visible {
    box-shadow: none;

    ${LinkCircle} {
      border: ${(props) => props.theme.newconstants.borderDarkThick};
    }
  }
`;

const LinkSmallAccent = styled(Link)`
  ${fontSizeSmallResp}
  ${fontFamilyMedium}
  ${fontColorAccent}

  &:focus-visible {
    box-shadow: ${(props) => props.theme.newconstants.keyNavFocus};
  }
`;

export const LinkSmallResp = ({ text, url, color, external }: LinkProps): JSX.Element => (
  <span>
    {external ? (
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: color === 'light' ? '#fff' : '#000' }}>
        <TxtSmallBoldDarkResp>{text}</TxtSmallBoldDarkResp>
      </a>
    ) : (
      <LinkUnderline to={url} color={color}>
        <TxtSmallBoldDarkResp>{text}</TxtSmallBoldDarkResp>
      </LinkUnderline>
    )}
  </span>
);

export const LinkMediumRespUnderline = ({ text, url, color, external }: LinkProps): JSX.Element => (
  <span>
    {external ? (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <LinkMediumResp>{text}</LinkMediumResp>
      </a>
    ) : (
      <LinkUnderline to={url} color={color}>
        <LinkMediumResp>{text}</LinkMediumResp>
      </LinkUnderline>
    )}
  </span>
);

export const LinkMediumRespInline = ({ text, url, color, external }: LinkProps): JSX.Element => (
  <span>
    {external ? (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <LinkMediumResp>{text}</LinkMediumResp>
      </a>
    ) : (
      <LinkUnderlineInline to={url} color={color}>
        <LinkMediumResp>{text}</LinkMediumResp>
      </LinkUnderlineInline>
    )}
  </span>
);

export const LinkMediumRespNoUnderline = ({ text, url, color, external }: LinkProps): JSX.Element =>
  external ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <LinkNoUnderline as="span" color={color}>
        <TxtMediumRegularLightResp>{text}</TxtMediumRegularLightResp>
      </LinkNoUnderline>
    </a>
  ) : (
    <LinkNoUnderline to={url} color={color}>
      <TxtMediumRegularLightResp>{text}</TxtMediumRegularLightResp>
    </LinkNoUnderline>
  );

export const LinkLargeNoUnderline = ({ text, url, external, onLinkClick }: LinkProps): JSX.Element =>
  external ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <LinkNoUnderline as="span">
        <LinkTxtLargeDarkStatic>{text}</LinkTxtLargeDarkStatic>
      </LinkNoUnderline>
    </a>
  ) : (
    <LinkNoUnderline to={url} onClick={onLinkClick}>
      <LinkTxtLargeDarkStatic>{text}</LinkTxtLargeDarkStatic>
    </LinkNoUnderline>
  );

export const IconLink = ({ url, external, onLinkClick }: LinkProps): JSX.Element =>
  external ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <ExternalLinkStyled>
        <LinkCircle width={4.5} height={4.5}>
          <ArrowRightIconDark width={3} height={3} />
        </LinkCircle>
      </ExternalLinkStyled>
    </a>
  ) : (
    <IconCircleLink to={url} onClick={onLinkClick}>
      <LinkCircle width={4.5} height={4.5}>
        <ArrowRightIconDark width={3} height={3} />
      </LinkCircle>
    </IconCircleLink>
  );

export const ScrollLinkAccent = ({ url, text }: LinkProps): JSX.Element => (
  <LinkSmallAccent
    to=""
    as="button"
    onClick={() => document.getElementById(url)?.scrollIntoView()}
    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
  >
    {text}
  </LinkSmallAccent>
);
